import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_AUDIO_BYTES, RECORD_CAP_SECONDS, baseAudioType, formatDuration, newAudioNote,
} from '../utils/audioNotes';
import { localIsoWithOffset } from '../utils/specimenCode';

/*
 * Voice notes, recorded here and kept until the form they belong to saves
 * them (utils/audioNotes.js says how each goes up).
 *
 * The microphone through getUserMedia and MediaRecorder, in the first type
 * this browser records of WebM/Opus, Ogg and MP4 -- the types the server
 * keeps (routes/audio_notes.py). A visible timer, and a stop of its own at
 * `capSeconds` (three minutes). Each note can be played back and deleted
 * before anything is saved; there may be several.
 *
 * Recording needs a secure context (HTTPS or localhost) and a browser that
 * has MediaRecorder. A phone on the desktop's plain http://<LAN-IP> has
 * neither, so there the control is a file input with `capture`, which opens
 * the phone's own recorder -- and a recording already on the device can
 * always be attached. A refused microphone falls back the same way.
 *
 * Nothing records once the component is gone: a recording still running is
 * stopped and dropped, and the microphone's tracks are stopped, so the
 * browser's recording indicator goes out with the form.
 *
 * Props:
 *   value      the notes so far: [{ id, blob, contentType, durationS, recordedAt }]
 *   onChange   (nextNotes) -- the whole list
 *   disabled   nothing can be added or removed (a save is under way)
 *   capSeconds where recording stops by itself
 *   testId     prefix for the test ids (default "voice")
 *   hold       one button that records while it is held and stops on
 *              release (the chat composer). A tap, Enter or Space starts
 *              it instead, and the next one stops it: the button that does
 *              what the gesture does (WCAG 2.5.1)
 *   compact    no heading, hint or list: the caller shows what was recorded
 */
export const HOLD_MS = 350;

/** The recorder's type: the first of the server's this browser records. */
const PREFERRED = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg', 'audio/mp4'];

export function pickMimeType(Recorder = (typeof MediaRecorder !== 'undefined' ? MediaRecorder : undefined)) {
  if (!Recorder || typeof Recorder.isTypeSupported !== 'function') return '';
  return PREFERRED.find((type) => {
    try {
      return Recorder.isTypeSupported(type);
    } catch {
      return false;
    }
  }) || '';
}

/** Whether this page can record at all. */
export function canRecordHere() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (window.isSecureContext === false) return false;
  return typeof MediaRecorder !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function';
}

export default function VoiceNoteRecorder({
  value = [], onChange, disabled = false, capSeconds = RECORD_CAP_SECONDS, testId = 'voice', hold = false,
  compact = false,
}) {
  const { t } = useTranslation('voice');
  const [recordable] = useState(canRecordHere);
  const [fallback, setFallback] = useState(!recordable);
  const [state, setState] = useState('idle'); // idle | starting | recording
  const [elapsed, setElapsed] = useState(0);
  const [message, setMessage] = useState(null); // { tone: 'warn' | 'note', text }
  const recorder = useRef(null);
  const stream = useRef(null);
  const timer = useRef(null);
  const live = useRef(true);
  const fileInput = useRef(null);
  // The latest list and handler, for a recording that ends after a re-render.
  const latest = useRef({ value, onChange });
  latest.current = { value, onChange };
  const urls = useRef(new Map());
  // Hold to record: when the press began, whether it started the recording,
  // whether the click that follows came from a pointer, and a release that
  // came before the microphone was ready.
  const pressedAt = useRef(null);
  const startedByPress = useRef(false);
  const fromPointer = useRef(false);
  const stopWhenReady = useRef(false);

  const cap = formatDuration(capSeconds);

  const stopTracks = () => {
    (stream.current?.getTracks?.() || []).forEach((track) => { try { track.stop(); } catch { /* gone */ } });
    stream.current = null;
  };

  useEffect(() => {
    live.current = true;
    const made = urls.current;   // one Map for the component's life
    return () => {
      live.current = false;
      clearInterval(timer.current);
      const r = recorder.current;
      recorder.current = null;
      if (r && r.state !== 'inactive') {
        r.onstop = null;
        try { r.stop(); } catch { /* already stopped */ }
      }
      stopTracks();
      made.forEach((url) => URL.revokeObjectURL?.(url));
      made.clear();
    };
  }, []);

  // A played-back note's address, made once and let go when it is removed.
  const ids = value.map((n) => n.id).join(',');
  useEffect(() => {
    const keep = new Set(value.map((n) => n.id));
    urls.current.forEach((url, id) => {
      if (!keep.has(id)) {
        URL.revokeObjectURL?.(url);
        urls.current.delete(id);
      }
    });
  }, [ids]); // eslint-disable-line react-hooks/exhaustive-deps
  const urlOf = (note) => {
    if (!note.blob || typeof URL.createObjectURL !== 'function') return '';
    if (!urls.current.has(note.id)) urls.current.set(note.id, URL.createObjectURL(note.blob));
    return urls.current.get(note.id);
  };

  const add = (note) => latest.current.onChange?.([...latest.current.value, note]);

  const stop = () => {
    clearInterval(timer.current);
    const r = recorder.current;
    if (r && r.state !== 'inactive') {
      try { r.stop(); } catch { /* already stopped */ }
    }
  };

  const start = async () => {
    if (disabled || state !== 'idle') return;
    setMessage(null);
    stopWhenReady.current = false;
    setState('starting');
    let media;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      if (!live.current) return;
      setState('idle');
      setFallback(true);
      setMessage({ tone: 'warn', text: t(e?.name === 'NotAllowedError' || e?.name === 'SecurityError' ? 'denied' : 'unavailable') });
      return;
    }
    if (!live.current) {
      (media.getTracks?.() || []).forEach((track) => track.stop());
      return;
    }
    stream.current = media;
    const mime = pickMimeType();
    let r;
    try {
      r = mime ? new MediaRecorder(media, { mimeType: mime }) : new MediaRecorder(media);
    } catch {
      try {
        r = new MediaRecorder(media);
      } catch {
        stopTracks();
        setState('idle');
        setFallback(true);
        setMessage({ tone: 'warn', text: t('unavailable') });
        return;
      }
    }
    const chunks = [];
    const began = Date.now();
    const recordedAt = localIsoWithOffset(new Date(began));
    let capped = false;
    r.ondataavailable = (event) => { if (event.data && event.data.size) chunks.push(event.data); };
    r.onstop = () => {
      clearInterval(timer.current);
      stopTracks();
      recorder.current = null;
      if (!live.current) return;
      setState('idle');
      setElapsed(0);
      const type = baseAudioType(r.mimeType || mime) || 'audio/webm';
      const blob = new Blob(chunks, { type });
      if (!blob.size) {
        setMessage({ tone: 'warn', text: t('empty') });
        return;
      }
      if (blob.size > MAX_AUDIO_BYTES) {
        setMessage({ tone: 'warn', text: t('tooLarge') });
        return;
      }
      if (capped) setMessage({ tone: 'note', text: t('capped', { cap }) });
      add(newAudioNote({
        blob, contentType: type, durationS: Math.min(capSeconds, (Date.now() - began) / 1000), recordedAt,
      }));
    };
    recorder.current = r;
    try {
      r.start(1000);
    } catch {
      recorder.current = null;
      stopTracks();
      setState('idle');
      setFallback(true);
      setMessage({ tone: 'warn', text: t('unavailable') });
      return;
    }
    setElapsed(0);
    setState('recording');
    timer.current = setInterval(() => {
      const seconds = (Date.now() - began) / 1000;
      setElapsed(seconds);
      if (seconds >= capSeconds) {
        capped = true;
        stop();
      }
    }, 250);
    // Let go before the microphone answered: what was held is what is kept.
    if (stopWhenReady.current) {
      stopWhenReady.current = false;
      stop();
    }
  };

  const holdDown = (event) => {
    if (disabled || (event.button ?? 0) > 0) return;
    fromPointer.current = true;
    if (state !== 'idle') return;          // a press while recording: its click stops it
    pressedAt.current = Date.now();
    startedByPress.current = true;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* not capturable */ }
    start();
  };
  const holdUp = () => {
    const at = pressedAt.current;
    pressedAt.current = null;
    if (at === null || Date.now() - at < HOLD_MS) return;   // a tap: it records until the next one
    if (recorder.current && recorder.current.state !== 'inactive') stop();
    else stopWhenReady.current = true;
  };
  const holdClick = () => {
    const pointer = fromPointer.current;
    fromPointer.current = false;
    if (pointer && startedByPress.current) {   // the click ending the press that started it
      startedByPress.current = false;
      return;
    }
    startedByPress.current = false;
    if (state === 'idle') start();
    else if (state === 'recording') stop();
    else if (state === 'starting') stopWhenReady.current = true;
  };

  const attach = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setMessage(null);
    const type = baseAudioType(file.type, file.name);
    if (!type) return setMessage({ tone: 'warn', text: t('unsupported') });
    if (file.size > MAX_AUDIO_BYTES) return setMessage({ tone: 'warn', text: t('tooLarge') });
    if (!file.size) return setMessage({ tone: 'warn', text: t('empty') });
    // When a picked file was recorded is its own business: it says only
    // when it was last changed, which is close enough to say nothing.
    add(newAudioNote({ blob: file, contentType: type, durationS: null, recordedAt: null }));
    return undefined;
  };

  const remove = (id) => latest.current.onChange?.(latest.current.value.filter((n) => n.id !== id));

  const recording = state === 'recording';
  const timerShown = recording && (
    <>
      <span style={vs.live} aria-hidden="true">●</span>
      <span role="timer" aria-live="off" aria-label={t('recording')} style={vs.timer} dir="ltr"
        data-testid={`${testId}-timer`}>
        {t('timer', { elapsed: formatDuration(elapsed), cap })}
      </span>
    </>
  );
  const named = compact ? { 'aria-label': t('title') } : { 'aria-labelledby': `${testId}-title` };
  return (
    <section className="voice-notes" style={vs.box} {...named} data-testid={testId}>
      {!compact && <h4 id={`${testId}-title`} style={vs.title}>{t('title')}</h4>}
      {!compact && <p style={vs.hint}>{fallback ? t('fallbackHint') : t('hint', { cap })}</p>}

      <div style={vs.row}>
        {hold && !fallback ? (
          <>
            <button type="button" disabled={disabled} aria-pressed={recording}
              onPointerDown={holdDown} onPointerUp={holdUp} onPointerCancel={holdUp} onClick={holdClick}
              onContextMenu={(e) => e.preventDefault()} title={t('tip.hold')} data-testid={`${testId}-hold`}
              style={{ ...(recording ? vs.stop : vs.primary), ...vs.holdable }}>
              {state === 'starting' ? t('starting') : recording ? t('holdStop') : t('hold')}
            </button>
            {timerShown}
          </>
        ) : (
          <>
            {!fallback && !recording && (
              <button type="button" style={vs.primary} onClick={start} disabled={disabled || state === 'starting'}
                title={t('tip.record')} data-testid={`${testId}-record`}>
                {state === 'starting' ? t('starting') : t('record')}
              </button>
            )}
            {recording && (
              <button type="button" style={vs.stop} onClick={stop} title={t('tip.stop')} data-testid={`${testId}-stop`}>
                {t('stop')}
              </button>
            )}
            {timerShown}
          </>
        )}
        {(!compact || fallback) && (
          <button type="button" style={fallback ? vs.primary : vs.secondary} disabled={disabled || recording}
            onClick={() => fileInput.current?.click()} title={t('tip.attach')} data-testid={`${testId}-attach`}>
            {t('attach')}
          </button>
        )}
        {/* `capture` only where this page cannot record: there it opens the
            phone's own recorder. Elsewhere it attaches a file already here. */}
        <input ref={fileInput} type="file" accept="audio/*" {...(fallback ? { capture: true } : {})}
          style={{ display: 'none' }} tabIndex={-1} aria-hidden="true" onChange={attach}
          data-testid={`${testId}-file`} />
      </div>

      {message && (
        <p role={message.tone === 'warn' ? 'alert' : 'status'} style={message.tone === 'warn' ? vs.warn : vs.hint}
          data-testid={`${testId}-message`}>
          {message.text}
        </p>
      )}

      {!compact && value.length > 0 && (
        <ol style={vs.list} data-testid={`${testId}-list`}>
          {value.map((note, i) => (
            <li key={note.id} style={vs.item} data-testid={`${testId}-note-${i}`}>
              <span style={vs.label}>{t('noteLabel', { n: i + 1 })}</span>
              {/* A voice note has no captions to offer; its label names it. */}
              <audio controls preload="metadata" src={urlOf(note)} style={vs.audio}
                aria-label={t('noteLabel', { n: i + 1 })} />
              {Number.isFinite(note.durationS) && (
                <span style={vs.length} dir="ltr">{t('length', { duration: formatDuration(note.durationS) })}</span>
              )}
              <button type="button" style={vs.remove} disabled={disabled} onClick={() => remove(note.id)}
                title={t('tip.delete')} aria-label={t('delete', { n: i + 1 })} data-testid={`${testId}-delete-${i}`}>
                ×
              </button>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

const vs = {
  box: { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 },
  title: { margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--c-text-fg)' },
  hint: { margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)', lineHeight: 1.45 },
  warn: { margin: 0, fontSize: 13, color: 'var(--c-danger-fg)', lineHeight: 1.45 },
  row: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' },
  primary: {
    minHeight: 44, padding: '0 16px', border: 'none', borderRadius: 10, background: 'var(--c-primary)',
    color: 'var(--c-on-accent)', font: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  secondary: {
    minHeight: 44, padding: '0 14px', border: '1px solid var(--c-border-strong)', borderRadius: 10,
    background: 'var(--c-surface)', color: 'var(--c-text-fg)', font: 'inherit', fontSize: 14, cursor: 'pointer',
  },
  stop: {
    minHeight: 44, padding: '0 16px', border: 'none', borderRadius: 10, background: 'var(--c-danger)',
    color: 'var(--c-on-accent)', font: 'inherit', fontSize: 15, fontWeight: 600, cursor: 'pointer',
  },
  live: { color: 'var(--c-danger-fg)', fontSize: 14 },
  // A long press records: no text selection, no callout menu, no scrolling under the finger.
  holdable: { touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' },
  timer: { fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 600, color: 'var(--c-text-fg)' },
  list: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  item: {
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 8,
    border: '1px solid var(--c-border)', background: 'var(--c-bg-subtle)',
  },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--c-text-fg)' },
  audio: { flex: '1 1 200px', minWidth: 0, maxWidth: '100%', height: 36 },
  length: { fontSize: 12, color: 'var(--c-text-muted-fg)', fontVariantNumeric: 'tabular-nums' },
  remove: {
    minWidth: 44, minHeight: 44, border: '1px solid var(--c-border-strong)', borderRadius: 8,
    background: 'var(--c-surface)', color: 'var(--c-text-fg)', fontSize: 18, cursor: 'pointer',
  },
};
